import { useState } from "react";
import { Check, User, MessageCircle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Section } from "@/components/Shared";
import { waLink, WHATSFORM_URL } from "@/data/site";
import { SERVICES } from "@/data/services";
import { PSYCHOLOGISTS } from "@/data/psychologists";
import { trackEvent } from "@/lib/analytics";


const EMPTY_BOOKING = { service: "", psychologist: "", date: "", time: "", name: "", phone: "", email: "", message: "" };

export const useBookingForm = (lang) => {
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);
  const [data, setData] = useState(EMPTY_BOOKING);
  const setField = (key, value) => setData((d) => ({ ...d, [key]: value }));
  const canNext = canProceed(step, data);

  const goNext = () => {
    if (!canNext) return;
    if (step === 0) trackEvent("appointment_started", { service: data.service });
    setStep((s) => s + 1);
  };
  const goBack = () => setStep((s) => s - 1);

  const submit = async () => {
    setSending(true);
    try {
      const message = buildWaText(lang, data);
      window.open(waLink(message), "_blank", "noopener,noreferrer");
      trackEvent("appointment_completed", { service: data.service });
      setDone(true);
    } finally {
      setSending(false);
    }
  };

  return { step, done, sending, data, setField, canNext, goNext, goBack, submit };
};

export const TIMES = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

export const inputCls = "w-full rounded-xl border border-[#E5E1D8] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6B53]/40 focus:border-[#4A6B53]";

export const optBtn = (selected) =>
  `w-full text-left rounded-xl border px-5 py-4 text-sm font-medium transition-colors ${
    selected ? "border-[#4A6B53] bg-[#4A6B53]/8 text-[#1F2937]" : "border-[#E5E1D8] bg-white text-[#4B5563] hover:border-[#4A6B53]/50"
  }`;

export const canProceed = (step, d) => {
  if (step === 0) return !!d.service;
  if (step === 2) return !!d.date && !!d.time;
  if (step === 3) return !!d.name && !!d.phone;
  return true;
};

export const buildWaText = (lang, d) =>
  lang === "id"
    ? `Halo Bali Empower, saya ${d.name} ingin membuat janji konsultasi.\nLayanan: ${d.service}\nPsikolog: ${d.psychologist || "Rekomendasi tim"}\nTanggal: ${d.date}\nWaktu: ${d.time}\nTelepon: ${d.phone}`
    : `Hello Bali Empower, I am ${d.name} and would like to book a consultation.\nService: ${d.service}\nPsychologist: ${d.psychologist || "Team recommendation"}\nDate: ${d.date}\nTime: ${d.time}\nPhone: ${d.phone}`;

const stepCircleCls = (i, step) => {
  if (i < step) return "bg-[#4A6B53] text-white";
  if (i === step) return "bg-[#4A6B53]/15 text-[#4A6B53] ring-2 ring-[#4A6B53]";
  return "bg-[#F2EFE9] text-[#4B5563]";
};

export const Stepper = ({ steps, step }) => (
  <div className="flex items-center gap-2 mb-10" data-testid="booking-stepper">
    {steps.map((label, i) => (
      <div key={label} className="flex items-center gap-2 flex-1">
        <div className={`w-8 h-8 shrink-0 rounded-full flex items-center justify-center text-xs font-semibold ${stepCircleCls(i, step)}`}>
          {i < step ? <Check size={14} /> : i + 1}
        </div>
        {i < steps.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-[#4A6B53]" : "bg-[#E5E1D8]"}`} />}
      </div>
    ))}
  </div>
);

export const StepService = ({ lang, data, setField }) => (
  <div className="grid sm:grid-cols-2 gap-3">
    {SERVICES.map((s) => (
      <button key={s.key} data-testid={`booking-service-${s.key}`}
        className={optBtn(data.service === s.name[lang])}
        onClick={() => setField("service", s.name[lang])}>
        {s.name[lang]}
      </button>
    ))}
  </div>
);

export const StepPsych = ({ lang, m, data, setField }) => (
  <div className="space-y-3">
    <button data-testid="booking-psych-any" className={optBtn(data.psychologist === "")}
      onClick={() => setField("psychologist", "")}>
      {m.anyPsych}
    </button>
    {PSYCHOLOGISTS.map((psy, i) => (
      <button key={psy.slug} data-testid={`booking-psych-${i}`}
        className={optBtn(data.psychologist === psy.name[lang])}
        onClick={() => setField("psychologist", psy.name[lang])}>
        <span className="inline-flex items-center gap-2"><User size={15} /> {psy.name[lang]}</span>
      </button>
    ))}
  </div>
);

export const StepSchedule = ({ m, data, setField }) => (
  <div className="space-y-6">
    <div>
      <label className="text-sm font-medium text-[#1F2937]">{m.date}</label>
      <input type="date" className={inputCls} value={data.date} data-testid="booking-date-input"
        min={new Date().toISOString().split("T")[0]}
        onChange={(e) => setField("date", e.target.value)} />
    </div>
    <div>
      <label className="text-sm font-medium text-[#1F2937]">{m.time}</label>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {TIMES.map((tm) => (
          <button key={tm} data-testid={`booking-time-${tm}`}
            className={`rounded-xl border py-2.5 text-sm font-medium transition-colors ${
              data.time === tm ? "border-[#4A6B53] bg-[#4A6B53] text-white" : "border-[#E5E1D8] bg-white text-[#4B5563] hover:border-[#4A6B53]/50"
            }`}
            onClick={() => setField("time", tm)}>
            {tm}
          </button>
        ))}
      </div>
      <p className="mt-3 text-xs text-[#4B5563] italic">{m.scheduleNote}</p>
    </div>
  </div>
);

const ContactField = ({ label, value, onChange, testId, type = "text", textarea }) => (
  <div>
    <label className="text-sm font-medium text-[#1F2937]">{label}</label>
    {textarea ? (
      <textarea rows={3} className={inputCls} value={value} data-testid={testId}
        onChange={(e) => onChange(e.target.value)} />
    ) : (
      <input type={type} className={inputCls} value={value} data-testid={testId}
        onChange={(e) => onChange(e.target.value)} />
    )}
  </div>
);

export const StepContact = ({ m, data, setField }) => (
  <div className="space-y-4">
    <ContactField label={m.name} value={data.name} testId="booking-name-input" onChange={(v) => setField("name", v)} />
    <ContactField label={m.phoneField} value={data.phone} testId="booking-phone-input" onChange={(v) => setField("phone", v)} />
    <ContactField label={m.email} value={data.email} type="email" testId="booking-email-input" onChange={(v) => setField("email", v)} />
    <ContactField label={m.notes} value={data.message} textarea testId="booking-notes-input" onChange={(v) => setField("message", v)} />
  </div>
);

export const StepSummary = ({ m, data }) => {
  const rows = [
    [m.service, data.service],
    [m.psychologist, data.psychologist || m.anyPsych],
    [m.date, data.date],
    [m.time, data.time],
    [m.name, data.name],
    [m.phoneField, data.phone],
  ];
  return (
    <div className="rounded-2xl border border-[#E5E1D8] bg-white p-8 space-y-3 text-sm" data-testid="booking-summary">
      <h3 className="font-display text-lg text-[#1F2937]">{m.summary}</h3>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-4 border-b border-[#F2EFE9] pb-2">
          <span className="text-[#4B5563]">{k}</span>
          <span className="font-medium text-[#1F2937] text-right">{v}</span>
        </div>
      ))}
      <p className="text-xs text-[#4B5563] italic pt-2">{m.scheduleNote}</p>
    </div>
  );
};

export const SuccessScreen = ({ lang, m, meta, waText }) => (
  <div data-testid="booking-success">
    <Seo title={meta.title} description={meta.desc} lang={lang} />
    <Section>
      <div className="max-w-xl mx-auto text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#4A6B53]/10 flex items-center justify-center">
          <Check size={28} className="text-[#4A6B53]" />
        </div>
        <h1 className="mt-6 text-3xl sm:text-4xl tracking-tight text-[#1F2937]">{m.bookingSuccess}</h1>
        <p className="mt-4 text-[#4B5563]">{m.bookingSuccessSub}</p>
        <a href={waLink(waText)} target="_blank" rel="noopener noreferrer" data-testid="booking-success-whatsapp"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4A6B53] text-white font-semibold px-8 py-4 hover:bg-[#3A5542] transition-colors">
          <MessageCircle size={17} /> {m.sendWa}
        </a>
        <div className="mt-4">
          <a href={WHATSFORM_URL} target="_blank" rel="noopener noreferrer" data-testid="booking-success-whatsform"
            className="inline-flex items-center gap-2 rounded-full border border-[#4A6B53] text-[#4A6B53] font-medium px-8 py-3.5 hover:bg-[#4A6B53]/5 transition-colors">
            {lang === "id" ? "Isi Formulir Resmi WhatsApp (WhatsForm)" : "Fill the Official WhatsApp Form (WhatsForm)"}
          </a>
        </div>
      </div>
    </Section>
  </div>
);
